/**
 * What of yours is running, wherever you are in the app ([[ROADMAP A.30]]).
 *
 * **Why a store rather than page state.** The page that starts a long run holds
 * its job id and can poll it — but the promise is that you can leave, and a
 * reader who navigates away, opens a second tab or reloads holds nothing. So
 * this asks the backend "what of mine is running?" and any surface can render
 * the answer, including one mounted after the work began.
 *
 * **It stops polling when there is nothing to watch.** An indicator that
 * heartbeats forever is a cost with no reader; this polls only while something
 * is running or freshly finished, and goes quiet otherwise.
 */
import { create } from 'zustand';

import portfolioService from '../services/portfolioService';

export type JobStatus = 'running' | 'done' | 'failed';

export interface ActiveJob {
  id: string;
  status: JobStatus;
  kind: string;
  /** "Risk analysis" / "Rebalance comparison" — the reader's words, from the server. */
  kindLabel: string;
  /** What it is running ON, usually a portfolio name. May be empty. */
  label: string;
  startedAt: string;
  completedAt: string;
  /** Why it will never finish. Empty unless `status === 'failed'`. */
  message: string;
  /** Where the answer lives, when the job produced an addressable one
   *  ([[BUGS #275]]). Absent for a risk analysis, whose result is a saved
   *  report the job never names. */
  reportId?: string;
}

/** How often to ask while something is running. Slow enough to be a heartbeat
 *  rather than a load, fast enough that "ready" does not feel stale. */
export const POLL_MS = 5000;

/** Give up after this many consecutive transport failures, rather than polling
 *  a dead endpoint forever — the [[BUGS #202]] lesson: an unbounded silent
 *  retry IS the "stuck" experience it was meant to prevent. */
export const MAX_CONSECUTIVE_FAILURES = 3;

interface ActiveJobsState {
  jobs: ActiveJob[];
  /** Ids the reader has dismissed, so a finished announcement stays dismissed
   *  for the rest of its short life rather than reappearing on the next poll. */
  dismissed: string[];
  polling: boolean;
  refresh: (getToken: () => Promise<string>) => Promise<void>;
  start: (getToken: () => Promise<string>) => void;
  stop: () => void;
  dismiss: (id: string) => void;
  reset: () => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;

export const useActiveJobsStore = create<ActiveJobsState>((set, get) => ({
  jobs: [],
  dismissed: [],
  polling: false,

  refresh: async (getToken) => {
    try {
      const res = await portfolioService.listActiveJobs(await getToken());
      failures = 0;
      const jobs = (res.success ? res.jobs ?? [] : []) as ActiveJob[];
      // Forget dismissals for jobs the server no longer reports, so the list
      // cannot grow without bound across a long session.
      const live = new Set(jobs.map((j) => j.id));
      set((s) => ({ jobs, dismissed: s.dismissed.filter((id) => live.has(id)) }));
    } catch {
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) get().stop();
    }
  },

  start: (getToken) => {
    if (get().polling) return;
    set({ polling: true });
    failures = 0;
    const tick = async () => {
      await get().refresh(getToken);
      if (!get().polling) return;
      // Only keep asking while there is something to say. A quiet app should
      // cost nothing.
      const watching = get().jobs.some((j) => j.status === 'running');
      const announcing = get().jobs.some(
        (j) => j.status !== 'running' && !get().dismissed.includes(j.id));
      timer = setTimeout(tick, watching || announcing ? POLL_MS : POLL_MS * 6);
    };
    void tick();
  },

  stop: () => {
    if (timer) { clearTimeout(timer); timer = null; }
    set({ polling: false });
  },

  dismiss: (id) => set((s) => ({ dismissed: [...s.dismissed, id] })),

  reset: () => {
    if (timer) { clearTimeout(timer); timer = null; }
    failures = 0;
    set({ jobs: [], dismissed: [], polling: false });
  },
}));

/** The jobs worth showing: everything running, plus finished ones not dismissed. */
export function visibleJobs(jobs: ActiveJob[], dismissed: string[]): ActiveJob[] {
  return jobs.filter((j) => j.status === 'running' || !dismissed.includes(j.id));
}
